/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { sanitizeEmailHtml } from "../core/sanitizer";
import { ParseHtml } from "../core/domAdapter";
import { resolveTheme } from "./theme";
import { EditorTheme } from "./theme";

/**
 * Enterprise-grade WYSIWYG editor for composing email bodies.
 *
 * Authors never see HTML: they use the toolbar (paragraph styles, live font/size
 * indicators, bold/italic/underline/strike, text + highlight color, alignment,
 * lists, links, tables with per-cell/header coloring, dividers, personalisation
 * tokens) and the editor keeps a live DOM. Pasted content (Word, Excel, Google
 * Docs, web pages) is run through sanitizeEmailHtml, which preserves the
 * source's actual font family, size (px/pt), weight and table border/fill
 * styling rather than collapsing everything to a handful of presets — paste
 * fidelity matters as much as manual formatting. The final email-safe
 * conversion happens again at assembly time (assembleEmail runs the sanitizer
 * on the body regardless, so pasted content is never trusted twice).
 *
 * Built on contentEditable + document.execCommand — deprecated but universally
 * supported, and the sanitizer canonicalises whatever the browser produces.
 */

export interface EditorToken {
  label: string;
  token: string;
}

const HIGHLIGHT_COLORS: [string, string][] = [
  ["None", "transparent"],
  ["Yellow", "#FEF3C7"],
  ["Orange", "#FDF1EE"],
  ["Green", "#ECFDF3"],
  ["Blue", "#EEF4FB"],
  ["Grey", "#F3F4F6"],
];
const FONT_SUGGESTIONS = [
  "Inter",
  "Arial",
  "Helvetica",
  "Calibri",
  "Cambria",
  "Garamond",
  "Georgia",
  "Times New Roman",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Segoe UI",
  "Roboto",
  "Courier New",
  "Consolas",
];
// Word's own classic font-size dropdown, verbatim — the size toolbar is
// designed to match what a Word/Docs author already expects, so it operates
// in points (pt), not pixels. Emails still render in px (matching every
// other size in this file and in sanitizer.ts's CSS), so pt is only the
// author-facing unit: applyFontSize converts pt -> px before it ever touches
// the DOM, and the live readout converts the caret's computed px back to pt.
const SIZE_SUGGESTIONS = [
  8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 60, 72,
];
const STATE_CMDS = [
  "bold",
  "italic",
  "underline",
  "strikeThrough",
  "insertUnorderedList",
  "insertOrderedList",
] as const;
const DEFAULT_FONT = "Inter";
/** Same 96dpi factor sanitizer.ts uses for pasted pt sizes, so a size round-trips exactly: paste a 10pt Word run, see "10" here, not a px-converted "13". */
const PX_PER_PT = 1.3333;
const pxToPt = (px: number) => Math.round(px / PX_PER_PT);
const ptToPx = (pt: number) => Math.round(pt * PX_PER_PT);
const DEFAULT_SIZE_PX = 14; // matches .hissa-rte's base font-size below
const DEFAULT_SIZE = pxToPt(DEFAULT_SIZE_PX); // ~11pt — the toolbar-facing default

/** Brand-colored swatches derive from theme.accent, so "Brand" always matches the actual theme. */
function textColorsFor(theme: EditorTheme): [string, string][] {
  return [
    ["Body", theme.bodyColor],
    ["Dark", theme.headingColor],
    ["Muted", "#6B7280"],
    ["Brand", theme.accent],
    ["Green", "#039855"],
    ["Red", "#B42318"],
    ["Blue", "#185FA5"],
    ["Amber", "#B45309"],
  ];
}
function headerColorsFor(theme: EditorTheme): [string, string][] {
  return [
    ["Default", "#F9FAFB"],
    ["Dark Navy", theme.headingColor],
    ["Brand", theme.accent],
    ["Slate", theme.bodyColor],
    ["Blue", "#185FA5"],
    ["Green", "#039855"],
    ["Amber", "#B45309"],
    ["Light Grey", "#F3F4F6"],
  ];
}

function editorCss(theme: EditorTheme): string {
  return `
.hissa-rte { font-family:'Inter',Arial,sans-serif; font-size:14px; line-height:22px; color:${theme.bodyColor}; }
.hissa-rte:focus { outline:none; }
.hissa-rte p { margin:0 0 12px; }
.hissa-rte h2 { margin:18px 0 10px; font-size:18px; font-weight:700; color:${theme.headingColor}; }
.hissa-rte h3 { margin:14px 0 8px; font-size:15px; font-weight:600; color:${theme.headingColor}; }
.hissa-rte ul, .hissa-rte ol { margin:0 0 12px; padding-left:22px; }
.hissa-rte li { margin:4px 0; }
.hissa-rte a { color:${theme.accent}; }
.hissa-rte hr { border:none; border-top:1px solid #E5E7EB; margin:16px 0; }
.hissa-rte blockquote { margin:0 0 12px; padding:8px 14px; border-left:3px solid ${theme.accent}; background:#F9FAFB; }
.hissa-rte table { width:100%; border-collapse:collapse; margin:0 0 14px; }
.hissa-rte th { padding:8px 10px; border:1px solid #E5E7EB; background:#F9FAFB; font-size:12px; font-weight:600; color:#6B7280; text-align:left; }
.hissa-rte td { padding:8px 10px; border:1px solid #E5E7EB; font-size:13px; }
`;
}

const btnStyle = (theme: EditorTheme, active = false): React.CSSProperties => ({
  height: 26,
  minWidth: 26,
  padding: "0 7px",
  borderRadius: 5,
  cursor: "pointer",
  border: active ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
  background: active ? theme.accentTint : "#fff",
  color: active ? theme.accent : theme.text,
  fontSize: 12,
  fontFamily: "Inter, sans-serif",
  lineHeight: "24px",
});
const comboStyle = (theme: EditorTheme): React.CSSProperties => ({
  height: 26,
  borderRadius: 5,
  border: `1px solid ${theme.border}`,
  background: "#fff",
  color: "#252F4A",
  fontSize: 12,
  fontFamily: "Inter, sans-serif",
  padding: "0 6px",
  outline: "none",
});
const selectStyle = (theme: EditorTheme): React.CSSProperties => ({
  height: 26,
  borderRadius: 5,
  border: `1px solid ${theme.border}`,
  background: "#fff",
  color: theme.text,
  fontSize: 12,
  fontFamily: "Inter, sans-serif",
  padding: "0 4px",
  cursor: "pointer",
});
const menuStyle = (theme: EditorTheme): React.CSSProperties => ({
  position: "absolute",
  top: 30,
  left: 0,
  zIndex: 30,
  background: "#fff",
  minWidth: 150,
  border: `1px solid ${theme.borderSubtle}`,
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  overflow: "hidden",
  padding: 6,
});

/** Auto-pick readable text (white or near-black) against an arbitrary hex fill — used for header/cell coloring. */
function contrastTextColor(hex: string): string {
  const c = hex.replace("#", "");
  if (c.length !== 6) return "#111827";
  const r = parseInt(c.slice(0, 2), 16),
    g = parseInt(c.slice(2, 4), 16),
    b = parseInt(c.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#111827" : "#FFFFFF";
}

function ToolbarButton({
  label,
  title,
  active,
  onClick,
  theme,
  style,
}: {
  label: React.ReactNode;
  title: string;
  active?: boolean;
  onClick: () => void;
  theme: EditorTheme;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection alive
      onClick={onClick}
      style={{ ...btnStyle(theme, active), ...style }}
    >
      {label}
    </button>
  );
}

function Divider({ theme }: { theme: EditorTheme }) {
  return (
    <div
      style={{
        width: 1,
        height: 18,
        background: theme.borderSubtle,
        margin: "0 2px",
        alignSelf: "center",
      }}
    />
  );
}

type MenuName =
  | ""
  | "color"
  | "highlight"
  | "align"
  | "table"
  | "tokens"
  | "headerColor"
  | "cellColor"
  | "image";

function ToolbarMenu({
  name,
  openMenu,
  onToggle,
  trigger,
  theme,
  children,
}: {
  name: MenuName;
  openMenu: MenuName;
  onToggle: (name: MenuName) => void;
  trigger: React.ReactNode;
  theme: EditorTheme;
  children: React.ReactNode;
}) {
  return (
    <div style={{ position: "relative" }}>
      <ToolbarButton
        label={trigger}
        title={name}
        active={openMenu === name}
        theme={theme}
        onClick={() => onToggle(openMenu === name ? "" : name)}
      />
      {openMenu === name && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 20 }}
            onClick={() => onToggle("")}
          />
          <div style={menuStyle(theme)} onMouseDown={(e) => e.preventDefault()}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Replace-or-insert popover for the Image toolbar button — same URL-or-upload pattern as ChromeEditor's LogoUpload. */
function ImagePopover({
  replacing,
  onUrl,
  onUpload,
  onRemove,
  theme,
}: {
  replacing: boolean;
  onUrl: (url: string) => void;
  onUpload: (file: File) => void;
  onRemove?: () => void;
  theme: EditorTheme;
}) {
  const [url, setUrl] = useState("");
  return (
    <div style={{ width: 220 }}>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>
        {replacing ? "Replace image" : "Insert image"}
      </div>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…/image.png"
          onClick={(e) => e.stopPropagation()}
          style={{ ...comboStyle(theme), flex: 1 }}
        />
        <button
          type="button"
          disabled={!url.trim()}
          onClick={() => url.trim() && onUrl(url.trim())}
          style={{
            ...btnStyle(theme),
            cursor: url.trim() ? "pointer" : "not-allowed",
            opacity: url.trim() ? 1 : 0.5,
          }}
        >
          Use
        </button>
      </div>
      <label
        style={{
          ...btnStyle(theme),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        Upload file
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onUpload(file);
          }}
          style={{ display: "none" }}
        />
      </label>
      {replacing && onRemove && (
        <div
          onClick={onRemove}
          style={{
            marginTop: 8,
            padding: "6px 4px",
            fontSize: 12,
            color: "#B42318",
            cursor: "pointer",
            textAlign: "center",
            borderRadius: 5,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FEF3F2")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          Remove image
        </div>
      )}
    </div>
  );
}

function SwatchGrid({
  colors,
  onPick,
  theme,
}: {
  colors: [string, string][];
  onPick: (color: string) => void;
  theme: EditorTheme;
}) {
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}
    >
      {colors.map(([label, color]) => (
        <div
          key={label}
          title={label}
          onClick={() => onPick(color)}
          style={{
            width: 26,
            height: 26,
            borderRadius: 5,
            cursor: "pointer",
            border: `1px solid ${theme.border}`,
            background:
              color === "transparent"
                ? "repeating-linear-gradient(45deg,#fff,#fff 4px,#F1F1F4 4px,#F1F1F4 8px)"
                : color,
          }}
        />
      ))}
    </div>
  );
}

export function RichTextEditor({
  html,
  onChange,
  tokens = [],
  theme: themeOverride,
  parseHtml,
  supportsImages = false,
}: {
  html: string;
  onChange: (html: string) => void;
  tokens?: EditorToken[];
  theme?: Partial<EditorTheme>;
  /** Node consumers rendering this server-side (unlikely, but supported) pass an adapter; browsers omit it. */
  parseHtml?: ParseHtml;
  /**
   * Shows the Image toolbar button (click an existing image to replace it, or insert a new one).
   * Off by default: the paste/editor sanitizer strips <img> entirely, so images added here would
   * silently vanish at assembly. Only pass true when the body bypasses that sanitizer (bodyMode: 'imported').
   */
  supportsImages?: boolean;
}) {
  const theme = resolveTheme(themeOverride);
  const textColors = textColorsFor(theme);
  const headerColors = headerColorsFor(theme);

  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>("");
  const savedRange = useRef<Range | null>(null);
  const fontFocused = useRef(false);
  const sizeFocused = useRef(false);
  const [activeCmds, setActiveCmds] = useState<Set<string>>(new Set());
  const [blockValue, setBlockValue] = useState("p");
  const [fontDraft, setFontDraft] = useState(DEFAULT_FONT);
  const [sizeDraft, setSizeDraft] = useState<number>(DEFAULT_SIZE);
  const [inTable, setInTable] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuName>("");
  const [tableHover, setTableHover] = useState<[number, number]>([0, 0]);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(
    null
  );

  // Sync external changes (e.g. template switch) without disturbing typing.
  useEffect(() => {
    const el = ref.current;
    if (el && html !== lastEmitted.current) {
      el.innerHTML = html;
      lastEmitted.current = html;
      setSelectedImage(null); // the DOM node it pointed at was just replaced
    }
  }, [html]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    lastEmitted.current = el.innerHTML;
    onChange(el.innerHTML);
  }, [onChange]);

  const getCell = useCallback((): HTMLTableCellElement | null => {
    const sel = window.getSelection();
    let n: Node | null | undefined = sel?.anchorNode;
    while (n && n !== ref.current) {
      if (n instanceof HTMLTableCellElement) return n;
      n = n.parentNode;
    }
    return null;
  }, []);

  const refreshState = useCallback(() => {
    // Focusing the font/size toolbar inputs is a real browser focus shift away
    // from the editor, which collapses the current selection as a side effect
    // (confirmed: a non-empty "Welcome" selection becomes empty the instant a
    // sibling <input> takes focus). That collapse fires its own selectionchange,
    // and without this guard this handler would treat it as "the user moved
    // their cursor" and overwrite savedRange with the now-useless collapsed
    // point — so by the time the font/size change is applied on blur, the
    // selection it's supposed to act on has already been destroyed. Ignoring
    // selectionchange entirely while either input is focused keeps savedRange
    // pointing at the real selection the user made before opening the toolbar.
    if (fontFocused.current || sizeFocused.current) return;
    const sel = window.getSelection();
    if (!sel?.anchorNode || !ref.current?.contains(sel.anchorNode)) return;
    savedRange.current = sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setActiveCmds(
      new Set(STATE_CMDS.filter((c) => document.queryCommandState(c)))
    );
    const block = (
      document.queryCommandValue("formatBlock") || "p"
    ).toLowerCase();
    setBlockValue(["h2", "h3"].includes(block) ? block : "p");
    setInTable(!!getCell());

    // Live font/size readout at the caret — like Word/Docs, so authors always
    // see what's actually applied, not just a static "Font"/"Size" placeholder.
    const anchorEl =
      sel.anchorNode.nodeType === Node.TEXT_NODE
        ? sel.anchorNode.parentElement
        : (sel.anchorNode as Element);
    if (anchorEl && ref.current.contains(anchorEl)) {
      const cs = window.getComputedStyle(anchorEl);
      if (!fontFocused.current) {
        const family = cs.fontFamily
          .split(",")[0]!
          .replace(/^['"]|['"]$/g, "")
          .trim();
        setFontDraft(family || DEFAULT_FONT);
      }
      if (!sizeFocused.current) {
        const px = parseFloat(cs.fontSize);
        setSizeDraft(Number.isFinite(px) && px > 0 ? pxToPt(px) : DEFAULT_SIZE);
      }
    }
  }, [getCell]);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshState);
    return () => document.removeEventListener("selectionchange", refreshState);
  }, [refreshState]);

  /**
   * Applies a specific Range, not whatever's currently saved — see the callers
   * in exec/applyFontSize for why that distinction matters.
   */
  const applyRange = useCallback((r: Range | null) => {
    if (!r) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }, []);

  /**
   * A caret at the end of the editor's content, for when there's no saved
   * selection to restore at all — e.g. an author opens a brand-new blank
   * template and picks a font/size from the toolbar before ever clicking
   * into the body. In that case savedRange.current is still its initial
   * null (refreshState has never fired for this editor), so applyRange(null)
   * is a no-op and execCommand runs against whatever default position
   * focus() happens to land on — which, for a never-focused element, isn't
   * reliably a real caret at all, so the format silently does nothing and
   * anything typed afterward falls back to the CSS default. Falling back to
   * an explicit end-of-content collapsed range guarantees execCommand always
   * has somewhere real to apply the format, so the very next character typed
   * picks it up.
   */
  const fallbackRange = useCallback((): Range | null => {
    if (!ref.current) return null;
    const r = document.createRange();
    r.selectNodeContents(ref.current);
    r.collapse(false);
    return r;
  }, []);

  const exec = useCallback(
    (cmd: string, value?: string) => {
      // Must be captured before focus() below: focusing the editor fires its own
      // onFocus -> refreshState (same as any other focus of this element), and
      // by this point fontFocused/sizeFocused are already false (blur runs
      // before the toolbar's onBlur handler calls into here) — so that
      // refreshState call is not suppressed, and it would overwrite
      // savedRange.current with whatever the selection collapsed to while the
      // toolbar input had focus, an instant before we go to restore it.
      // Reading it into a local now sidesteps that race entirely.
      const rangeToRestore = savedRange.current ?? fallbackRange();
      ref.current?.focus();
      applyRange(rangeToRestore);
      document.execCommand("styleWithCSS", false, "true");
      document.execCommand(cmd, false, value);
      emit();
      refreshState();
      setOpenMenu("");
    },
    [emit, refreshState, applyRange, fallbackRange, setOpenMenu]
  );

  // execCommand only offers 7 legacy size buckets — apply the largest, then rewrite the
  // <font size="7"> wrapper it produces into a span with the exact pixel size requested.
  // This is the standard workaround for arbitrary font sizes via execCommand. styleWithCSS
  // must be OFF for this specific call — with it on, Chrome emits a CSS keyword
  // (font-size: xxx-large) instead of a <font size="7"> tag, which our rewrite can't target.
  const applyFontSize = useCallback(
    (pt: number) => {
      const px = ptToPx(pt); // toolbar/author-facing unit is pt; the DOM (and email rendering) stays px throughout
      const rangeToRestore = savedRange.current ?? fallbackRange(); // see exec() — same capture-before-focus race and empty-editor fallback
      ref.current?.focus();
      applyRange(rangeToRestore);
      document.execCommand("styleWithCSS", false, "false");
      document.execCommand("fontSize", false, "7");
      ref.current?.querySelectorAll('font[size="7"]').forEach((f) => {
        const span = document.createElement("span");
        span.style.fontSize = `${px}px`;
        while (f.firstChild) span.appendChild(f.firstChild);
        f.replaceWith(span);
      });
      document.execCommand("styleWithCSS", false, "true");
      emit();
      refreshState();
    },
    [emit, refreshState, applyRange, fallbackRange]
  );

  const applyFontFamily = useCallback(
    (name: string) => {
      if (!name.trim()) return;
      exec("fontName", name.trim());
    },
    [exec]
  );

  // ── Tables ──────────────────────────────────────────────────────────────
  const insertTable = (rows: number, cols: number) => {
    const head = `<tr>${Array.from(
      { length: cols },
      (_, i) => `<th>Heading ${i + 1}</th>`
    ).join("")}</tr>`;
    const bodyRow = `<tr>${"<td><br></td>".repeat(cols)}</tr>`;
    exec(
      "insertHTML",
      `<table><tbody>${head}${bodyRow.repeat(rows)}</tbody></table><p><br></p>`
    );
  };

  const focusCell = (cell: HTMLTableCellElement) => {
    const r = document.createRange();
    r.selectNodeContents(cell);
    r.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
    refreshState();
  };

  const tableOp = (
    op: "rowBelow" | "colRight" | "delRow" | "delCol" | "delTable"
  ) => {
    const cell = getCell();
    const table = cell?.closest("table");
    if (!cell || !table) return;
    const row = cell.parentElement as HTMLTableRowElement;
    const idx = cell.cellIndex;

    if (op === "rowBelow") {
      const tr = document.createElement("tr");
      for (let i = 0; i < row.cells.length; i++) {
        const td = document.createElement("td");
        td.innerHTML = "<br>";
        tr.appendChild(td);
      }
      row.after(tr);
      focusCell(tr.cells[0]!);
    } else if (op === "colRight") {
      table.querySelectorAll("tr").forEach((tr) => {
        const anchor = tr.cells[Math.min(idx, tr.cells.length - 1)];
        const nc = document.createElement(
          anchor?.tagName === "TH" ? "th" : "td"
        );
        nc.innerHTML = "<br>";
        if (anchor) anchor.after(nc);
        else tr.appendChild(nc);
      });
    } else if (op === "delRow") {
      row.remove();
      if (!table.querySelector("tr")) table.remove();
    } else if (op === "delCol") {
      table.querySelectorAll("tr").forEach((tr) => tr.cells[idx]?.remove());
      if (!table.querySelector("td,th")) table.remove();
    } else {
      table.remove();
    }
    emit();
    setInTable(!!getCell());
  };

  /** Colors the header row (scope="header") or just the current cell (scope="cell"), with auto-contrast text. */
  const applyCellColor = (bg: string, scope: "header" | "cell") => {
    const cell = getCell();
    const table = cell?.closest("table");
    if (!cell || !table) return;
    let targets: HTMLTableCellElement[];
    if (scope === "header") {
      const rows = Array.from(table.rows);
      const headerRow =
        rows.find((r) => Array.from(r.cells).some((c) => c.tagName === "TH")) ||
        rows[0]!;
      targets = Array.from(headerRow.cells);
    } else {
      targets = [cell];
    }
    const clear = bg === "transparent";
    const textColor = clear ? "" : contrastTextColor(bg);
    targets.forEach((c) => {
      if (clear) {
        c.style.removeProperty("background-color");
        c.style.removeProperty("color");
      } else {
        c.style.backgroundColor = bg;
        c.style.color = textColor;
      }
    });
    emit();
    setOpenMenu("");
  };

  // Tab moves through table cells; Tab on the last cell appends a row.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const cell = getCell();
    const table = cell?.closest("table");
    if (!cell || !table) return;
    e.preventDefault();
    const cells = Array.from(
      table.querySelectorAll<HTMLTableCellElement>("th,td")
    );
    const i = cells.indexOf(cell);
    const next = e.shiftKey ? cells[i - 1] : cells[i + 1];
    if (next) focusCell(next);
    else if (!e.shiftKey) tableOp("rowBelow");
  };

  // Pasted content (Word, Excel, Google Docs, web pages) goes through sanitizeEmailHtml,
  // which preserves the source's actual font, size, weight and table styling rather
  // than discarding it — junk markup (scripts, classes, mso- cruft) still never lands.
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedHtml = e.clipboardData.getData("text/html");
    const pastedText = e.clipboardData.getData("text/plain");
    const clean = pastedHtml
      ? sanitizeEmailHtml(pastedHtml, { parseHtml })
      : pastedText
          .split("\n")
          .map((l) => l || "<br>")
          .join("<br>");
    document.execCommand("insertHTML", false, clean);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) exec("createLink", href);
    else exec("insertHTML", `<a href="${href}">${href}</a>`);
  };

  // ── Images (only wired up when supportsImages) ────────────────────────────
  const deselectImage = useCallback(() => {
    selectedImage?.style.removeProperty("outline");
    selectedImage?.style.removeProperty("outline-offset");
    setSelectedImage(null);
  }, [selectedImage]);

  const handleEditorClick = (e: React.MouseEvent) => {
    if (!supportsImages) return;
    const target = e.target as HTMLElement;
    if (target instanceof HTMLImageElement) {
      if (target !== selectedImage) {
        selectedImage?.style.removeProperty("outline");
        selectedImage?.style.removeProperty("outline-offset");
        target.style.outline = `2px solid ${theme.accent}`;
        target.style.outlineOffset = "1px";
        setSelectedImage(target);
      }
    } else if (selectedImage) {
      deselectImage();
    }
  };

  const applyImageSrc = (src: string) => {
    if (/^(javascript|vbscript):/i.test(src.trim())) return;
    if (selectedImage) {
      selectedImage.setAttribute("src", src);
      emit();
    } else {
      exec(
        "insertHTML",
        `<img src="${src.replace(/"/g, "&quot;")}" style="max-width:100%;">`
      );
    }
    setOpenMenu("");
  };

  const removeSelectedImage = () => {
    selectedImage?.remove();
    setSelectedImage(null);
    emit();
    setOpenMenu("");
  };

  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        background: "#fff",
      }}
    >
      <style>{editorCss(theme)}</style>
      <datalist id="rte-font-suggestions">
        {FONT_SUGGESTIONS.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>
      <datalist id="rte-size-suggestions">
        {SIZE_SUGGESTIONS.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>

      {/* ── Toolbar row 1: structure + live font/size ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          padding: "8px 8px 0",
          alignItems: "center",
        }}
      >
        <select
          value={blockValue}
          style={selectStyle(theme)}
          onMouseDown={refreshState}
          onChange={(e) => exec("formatBlock", e.target.value.toUpperCase())}
          title="Paragraph style"
        >
          <option value="p">Paragraph</option>
          <option value="h2">Heading</option>
          <option value="h3">Subheading</option>
        </select>

        {/* Editable combo box, not a fixed preset list — shows the real font at the caret
            (e.g. "Calibri" after a paste) and accepts any font name, like Word/Docs. */}
        <input
          list="rte-font-suggestions"
          value={fontDraft}
          title="Font — shows the font at your cursor; type any name"
          onFocus={() => {
            fontFocused.current = true;
          }}
          onChange={(e) => setFontDraft(e.target.value)}
          onBlur={(e) => {
            fontFocused.current = false;
            applyFontFamily(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          style={{ ...comboStyle(theme), width: 118 }}
        />
        <input
          type="number"
          list="rte-size-suggestions"
          value={sizeDraft}
          min={8}
          max={72}
          title="Size (pt) — shows the size at your cursor, same units as Word/Docs; type any value"
          onFocus={() => {
            sizeFocused.current = true;
          }}
          onChange={(e) => setSizeDraft(Number(e.target.value) || DEFAULT_SIZE)}
          onBlur={(e) => {
            sizeFocused.current = false;
            applyFontSize(
              Math.min(72, Math.max(8, Number(e.target.value) || DEFAULT_SIZE))
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          style={{ ...comboStyle(theme), width: 56 }}
        />
        <Divider theme={theme} />
        <ToolbarButton
          label="↺"
          title="Undo"
          theme={theme}
          onClick={() => exec("undo")}
        />
        <ToolbarButton
          label="↻"
          title="Redo"
          theme={theme}
          onClick={() => exec("redo")}
        />
        <Divider theme={theme} />
        <ToolbarButton
          label="Clear"
          title="Clear formatting"
          theme={theme}
          onClick={() => {
            exec("removeFormat");
            exec("formatBlock", "P");
          }}
        />
      </div>

      {/* ── Toolbar row 2: formatting ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          padding: 8,
          borderBottom: `1px solid ${theme.borderSubtle}`,
        }}
      >
        <ToolbarButton
          label={<b>B</b>}
          title="Bold"
          theme={theme}
          active={activeCmds.has("bold")}
          onClick={() => exec("bold")}
        />
        <ToolbarButton
          label={<i>I</i>}
          title="Italic"
          theme={theme}
          active={activeCmds.has("italic")}
          onClick={() => exec("italic")}
        />
        <ToolbarButton
          label={<u>U</u>}
          title="Underline"
          theme={theme}
          active={activeCmds.has("underline")}
          onClick={() => exec("underline")}
        />
        <ToolbarButton
          label={<s>S</s>}
          title="Strikethrough"
          theme={theme}
          active={activeCmds.has("strikeThrough")}
          onClick={() => exec("strikeThrough")}
        />
        <Divider theme={theme} />
        <ToolbarMenu
          name="color"
          openMenu={openMenu}
          onToggle={setOpenMenu}
          theme={theme}
          trigger={
            <span style={{ borderBottom: `3px solid ${theme.accent}` }}>A</span>
          }
        >
          <SwatchGrid
            colors={textColors}
            onPick={(c) => exec("foreColor", c)}
            theme={theme}
          />
        </ToolbarMenu>
        <ToolbarMenu
          name="highlight"
          openMenu={openMenu}
          onToggle={setOpenMenu}
          theme={theme}
          trigger={
            <span
              style={{
                background: "#FEF3C7",
                padding: "0 3px",
                borderRadius: 2,
              }}
            >
              A
            </span>
          }
        >
          <SwatchGrid
            colors={HIGHLIGHT_COLORS}
            onPick={(c) => exec("hiliteColor", c)}
            theme={theme}
          />
        </ToolbarMenu>
        <Divider theme={theme} />
        <ToolbarMenu
          name="align"
          openMenu={openMenu}
          onToggle={setOpenMenu}
          theme={theme}
          trigger="Align ▾"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(
              [
                ["Left", "justifyLeft"],
                ["Center", "justifyCenter"],
                ["Right", "justifyRight"],
              ] as const
            ).map(([label, cmd]) => (
              <div
                key={cmd}
                onClick={() => exec(cmd)}
                style={{
                  padding: "6px 10px",
                  fontSize: 12,
                  color: "#252F4A",
                  cursor: "pointer",
                  borderRadius: 5,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F0F2FF")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {label}
              </div>
            ))}
          </div>
        </ToolbarMenu>
        <Divider theme={theme} />
        <ToolbarButton
          label="• List"
          title="Bulleted list"
          theme={theme}
          active={activeCmds.has("insertUnorderedList")}
          onClick={() => exec("insertUnorderedList")}
        />
        <ToolbarButton
          label="1. List"
          title="Numbered list"
          theme={theme}
          active={activeCmds.has("insertOrderedList")}
          onClick={() => exec("insertOrderedList")}
        />
        <Divider theme={theme} />
        <ToolbarButton
          label="Link"
          title="Insert link"
          theme={theme}
          onClick={insertLink}
        />
        <ToolbarButton
          label="Quote"
          title="Quote block"
          theme={theme}
          onClick={() => exec("formatBlock", "BLOCKQUOTE")}
        />
        <ToolbarButton
          label="—"
          title="Divider line"
          theme={theme}
          onClick={() => exec("insertHTML", "<hr><p><br></p>")}
        />
        {supportsImages && (
          <ToolbarMenu
            name="image"
            openMenu={openMenu}
            onToggle={setOpenMenu}
            theme={theme}
            trigger={selectedImage ? "Replace Image" : "Image"}
          >
            <ImagePopover
              replacing={!!selectedImage}
              onUrl={applyImageSrc}
              onUpload={async (file) =>
                applyImageSrc(await readFileAsDataUri(file))
              }
              onRemove={selectedImage ? removeSelectedImage : undefined}
              theme={theme}
            />
          </ToolbarMenu>
        )}
        <Divider theme={theme} />
        <ToolbarMenu
          name="table"
          openMenu={openMenu}
          onToggle={setOpenMenu}
          theme={theme}
          trigger="⊞ Table ▾"
        >
          <div>
            <div
              style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}
            >
              {tableHover[0] > 0
                ? `${tableHover[0]} × ${tableHover[1]} table`
                : "Pick size (rows × columns)"}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, 16px)",
                gap: 3,
              }}
            >
              {Array.from({ length: 20 }, (_, i) => {
                const r = Math.floor(i / 5) + 1;
                const c = (i % 5) + 1;
                const hot = r <= tableHover[0] && c <= tableHover[1];
                return (
                  <div
                    key={i}
                    onMouseEnter={() => setTableHover([r, c])}
                    onClick={() => insertTable(r, c)}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      cursor: "pointer",
                      border: `1px solid ${theme.border}`,
                      background: hot ? theme.accentTint : "#fff",
                      borderColor: hot ? theme.accent : theme.border,
                    }}
                  />
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>
              A heading row is added automatically.
            </div>
          </div>
        </ToolbarMenu>
        {tokens.length > 0 && (
          <ToolbarMenu
            name="tokens"
            openMenu={openMenu}
            onToggle={setOpenMenu}
            theme={theme}
            trigger="Insert detail ▾"
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {tokens.map((t) => (
                <div
                  key={t.token}
                  onClick={() => exec("insertText", t.token)}
                  style={{
                    padding: "6px 10px",
                    cursor: "pointer",
                    borderRadius: 5,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#F0F2FF")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <div
                    style={{ fontSize: 12, fontWeight: 600, color: "#252F4A" }}
                  >
                    {t.label}
                  </div>
                  <div style={{ fontSize: 11, color: theme.textMuted }}>
                    {t.token}
                  </div>
                </div>
              ))}
            </div>
          </ToolbarMenu>
        )}
      </div>

      {/* ── Table tools (contextual) ── */}
      {inTable && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            padding: "6px 8px",
            background: "#FDF9F7",
            borderBottom: `1px solid ${theme.borderSubtle}`,
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.06em",
              color: theme.accent,
              textTransform: "uppercase",
              marginRight: 4,
            }}
          >
            Table
          </span>
          <ToolbarButton
            label="+ Row"
            title="Add row below"
            theme={theme}
            onClick={() => tableOp("rowBelow")}
          />
          <ToolbarButton
            label="+ Column"
            title="Add column right"
            theme={theme}
            onClick={() => tableOp("colRight")}
          />
          <ToolbarButton
            label="− Row"
            title="Delete row"
            theme={theme}
            onClick={() => tableOp("delRow")}
          />
          <ToolbarButton
            label="− Column"
            title="Delete column"
            theme={theme}
            onClick={() => tableOp("delCol")}
          />
          <ToolbarButton
            label="Delete table"
            title="Delete entire table"
            theme={theme}
            onClick={() => tableOp("delTable")}
          />
          <Divider theme={theme} />
          <ToolbarMenu
            name="headerColor"
            openMenu={openMenu}
            onToggle={setOpenMenu}
            theme={theme}
            trigger="Header Color ▾"
          >
            <div
              style={{
                fontSize: 11,
                color: theme.textMuted,
                marginBottom: 6,
                padding: "0 2px",
              }}
            >
              Colors the whole header row
            </div>
            <SwatchGrid
              colors={headerColors}
              onPick={(c) => applyCellColor(c, "header")}
              theme={theme}
            />
          </ToolbarMenu>
          <ToolbarMenu
            name="cellColor"
            openMenu={openMenu}
            onToggle={setOpenMenu}
            theme={theme}
            trigger="Cell Color ▾"
          >
            <div
              style={{
                fontSize: 11,
                color: theme.textMuted,
                marginBottom: 6,
                padding: "0 2px",
              }}
            >
              Colors just this cell
            </div>
            <SwatchGrid
              colors={HIGHLIGHT_COLORS}
              onPick={(c) => applyCellColor(c, "cell")}
              theme={theme}
            />
          </ToolbarMenu>
          <span style={{ fontSize: 10, color: theme.textMuted, marginLeft: 4 }}>
            Tab moves between cells
          </span>
        </div>
      )}

      {/* ── Editing surface ── */}
      <div
        ref={ref}
        className="hissa-rte"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={refreshState}
        onClick={handleEditorClick}
        style={{
          minHeight: 220,
          maxHeight: 380,
          overflow: "auto",
          padding: "12px 14px",
        }}
      />
    </div>
  );
}
